import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { formatDate } from '@/lib';
import { DeviceIconMap } from '@/routes/(tv)/tv/-model/model';
import { Loader, Lock, Trash, Tv, Warning } from '@nosinovacao/fe-icons';
import { Alert, Button, If, useI18n } from '@nosinovacao/nosid-mfe-common';
import { type DeviceManagementProp } from '../-model/props';

export function DeviceManagementLight({
  isLoading,
  devices,
  removeLoading,
  removeDevice,
  canDeleteAllDevices,
  removeAllDevices,
}: Readonly<DeviceManagementProp>) {
  const { t } = useI18n();
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
      <div className="flex-1 overflow-y-auto">
        <SkeletonLight loading={isLoading} className="h-24 rounded" count={5}>
          <If condition={!devices.length}>
            <span
              data-tid="text-no-devices"
              className="font-medium text-nos-neutral-900 font-text-p1"
            >
              {t('Tv.NoDevices')}
            </span>
          </If>
          <If condition={!!devices.length}>
            <div className="grid gap-4">
              {devices.map((device) => {
                const name = device.Name || t(`Tv.Device.DefaultName.${device.Type}`);
                const DeviceIcon = DeviceIconMap[device.Type] ?? Tv;
                return (
                  <div
                    key={device.Id}
                    className="grid grid-cols-[24px_1fr_24px] items-center gap-4 rounded border border-nos-neutral-200 p-4 [&_svg]:size-6"
                  >
                    <DeviceIcon width={32} height={32} />
                    <div className="grid gap-1">
                      <span
                        data-tid="text-device-name"
                        className="overflow-hidden text-ellipsis font-bold text-nos-neutral-900 font-text-p2"
                        title={name}
                      >
                        {name}
                      </span>
                      <span className="text-nos-neutral-500 font-text-p2">
                        {t('Tv.Device.FirstAccess')}: {formatDate(device.FirstAccess)}
                      </span>
                      <span className="text-nos-neutral-500 font-text-p2">
                        {t('Tv.Device.LastAccess')}: {formatDate(device.LastAccess)}
                      </span>
                      <If condition={device.IsLocked}>
                        <span className="text-nos-red font-text-p2">
                          {t('DeviceManagement.LockedTip.Title', {
                            date: formatDate(device.LockExpireDate),
                          })}
                        </span>
                      </If>
                    </div>
                    <If condition={!device.IsLocked}>
                      <Alert
                        data-tid="alert-delete-device"
                        skin="light"
                        title={t('DeviceManagement.DeleteDevicePrompt.Title')}
                        description={t('DeviceManagement.DeleteDevicePrompt.Description')}
                        className="z-[70]"
                        icon={<Warning width={48} height={48} className="text-nos-red" />}
                        overlayClassName="z-[70]"
                        buttons={(close) => (
                          <div className="mt-10 grid gap-4">
                            <Button
                              data-tid="button-cancel-delete-device"
                              skin="light"
                              type="button"
                              onClick={close}
                              disabled={removeLoading}
                            >
                              {t('Common.Cancel')}
                            </Button>
                            <Button
                              data-tid="button-confirm-delete-device"
                              skin="light"
                              destructive
                              disabled={removeLoading}
                              onClick={async () => {
                                await removeDevice(device);
                                close();
                              }}
                            >
                              <If
                                condition={removeLoading}
                                otherwise={t('DeviceManagement.DeleteDeviceButton')}
                              >
                                <Loader width={24} height={24} />
                              </If>
                            </Button>
                          </div>
                        )}
                      >
                        <button
                          data-tid="button-delete-device-trigger"
                          type="button"
                          className="text-nos-red"
                        >
                          <Trash />
                        </button>
                      </Alert>
                    </If>
                    <If condition={device.IsLocked}>
                      <Lock />
                    </If>
                  </div>
                );
              })}
            </div>
          </If>
        </SkeletonLight>
      </div>
      <If condition={canDeleteAllDevices}>
        <div className="w-full max-tablet:pb-8">
          <Alert
            data-tid="alert-delete-all-devices"
            skin="light"
            title={t('DeviceManagement.DeleteAllDevicesPrompt.Title')}
            description={t('DeviceManagement.DeleteAllDevicesPrompt.Description')}
            className="z-[70]"
            icon={<Warning width={48} height={48} className="text-nos-red" />}
            overlayClassName="z-[70]"
            buttons={(close) => (
              <div className="mt-10 grid gap-4">
                <Button
                  data-tid="button-cancel-delete-all"
                  skin="light"
                  type="button"
                  onClick={close}
                  disabled={removeLoading}
                >
                  {t('Common.Cancel')}
                </Button>
                <Button
                  data-tid="button-confirm-delete-all"
                  skin="light"
                  destructive
                  disabled={removeLoading}
                  onClick={async () => {
                    await removeAllDevices();
                    close();
                  }}
                >
                  <If
                    condition={removeLoading}
                    otherwise={t('DeviceManagement.DeleteAllDevicesButton')}
                  >
                    <Loader width={24} height={24} />
                  </If>
                </Button>
              </div>
            )}
          >
            <Button data-tid="button-delete-all-devices" skin="light" className="px-6" destructive>
              {t('DeviceManagement.DeleteAllDevicesButton')}
            </Button>
          </Alert>
        </div>
      </If>
    </div>
  );
}
